import { useEffect } from 'react'
import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import { treeStore } from '@/msgdeskRuntime/state'

export function useRuntimeUiFacade() {
  useEffect(() => {
    const runtime = getMsgDeskRuntime()
    const unwrapLegacyElement = (el: HTMLElement): HTMLElement => {
      return (el as unknown as { __MSG_REAL_ELEMENT__?: HTMLElement }).__MSG_REAL_ELEMENT__ || el
    }

    const fatalUnvirtualized = (op: string, el: HTMLElement) => {
      const detail = {
        op,
        id: el.id || null,
        contract: el.getAttribute('data-md-contract'),
        legacyClass: el.getAttribute('data-md-legacy-class'),
        className: el.getAttribute('class'),
        text: (el.textContent || '').trim().slice(0, 80),
        tagName: el.tagName?.toLowerCase(),
      }
      runtime.recordFallback(detail)
      if (runtime.isStrictVirtualDom()) {
        throw new Error(`[v2 strict] Unvirtualized visibility target: ${JSON.stringify(detail)}`)
      }
      return false
    }

    let nextVirtualNodeId = 1
    const virtualizeLegacyNode = (el: HTMLElement): string => {
      const existing = el.getAttribute('data-md-virtual-key')
      if (existing && treeStore.mutate.hasContract?.(existing)) return existing
      const parentContract = el.closest('[data-md-contract]')?.getAttribute('data-md-contract') || 'legacy'
      const key = `${parentContract}::node:${nextVirtualNodeId++}`
      el.setAttribute('data-md-virtual-key', key)
      treeStore.mutate.registerContract?.(key, el)
      return key
    }

    const updateVisibility = (el: HTMLElement, isVisible: boolean) => {
      const realEl = unwrapLegacyElement(el)
      const id = realEl.id || ''
      if (id.startsWith('mcss-li_id-cssm_')) {
        const elId = parseInt(id.replace('mcss-li_id-cssm_', ''), 10)
        if (!Number.isNaN(elId) && treeStore.getState().nodesByElId[elId]) {
          treeStore.mutate.setVisible(id, isVisible)
          return true
        }
      }
      const contract = realEl.getAttribute('data-md-contract')
      if (contract && treeStore.mutate.hasContract?.(contract as any)) {
        return treeStore.mutate.setContractVisible(contract as any, isVisible)
      }
      const key = virtualizeLegacyNode(realEl)
      if (treeStore.mutate.setContractVisible?.(key, isVisible)) return true
      return fatalUnvirtualized('setVisibleElement', realEl)
    }

    const prevUI = runtime.getReactUiFacade<any>() || {}

    runtime.installReactUiFacade({
      ...prevUI,
      registerSlot: prevUI.registerSlot || ((_slotId: string, _el: HTMLElement | null) => {}),
      setVisibleElement: (el: HTMLElement, isVisible: boolean) => updateVisibility(el, isVisible),
      toggleElement: (el: HTMLElement, isVisible?: boolean) => {
        const realEl = unwrapLegacyElement(el)
        const next = typeof isVisible === 'boolean' ? isVisible : runtime.browserWindow?.getComputedStyle(realEl).display === 'none'
        return updateVisibility(el, next)
      },
    })

    return () => {
      runtime.deleteGlobal('__MSG_REACT_UI__')
    }
  }, [])
}
